// @ts-strict-ignore
import dayjs from 'dayjs'

import { dayjsToString } from '~/utils/graphqlDataFormatters'

import FestivalListingBannerView from '~/components/Festival/FestivalListingBanner'
import {
  LeftImageBottomElement,
  LeftImageContainer,
  LeftImageTopElement,
} from '~/components/Festival/FestivalListingBanner/FestivalListingBannerShards'
import { bwStyle, rwpStyle } from '~/components/Festival/FestivalListingBanner/FestivalListingBannerUtils'
import { dateToDaysAndMonths } from '~/components/Festival/OngoingFestivalsFestival/OngoingFestivalsFestivalUtils'
import { OTHER_DATES_RESTAURANTS_LIMIT } from '~/components/Listing/ListingPageContent/ListingPageContentUtils'
import { addFringeSlots } from '~/components/Reservation/SlotSelectionPanel/SlotSelectionPanelUtils'
import { paths } from '~/constants'
import {
  FestivalCodesEnum,
  OngoingFestivalsListQueryResult,
  OtherDatesRestaurantsQuery,
  ReservableEnum,
  RestaurantsListQueryVariables,
  useOtherDatesRestaurantsQuery,
} from '~/generated/graphql'
import { gt } from '~/locale'

import { FilterSettings } from '../../ListingPageContent'
import { AllQueryVariables } from '../../ListingPageContent/ListingPageContentTypes'

export const PAGE_SIZE = 3

export const defaultFilterSettings: FilterSettings = {
  tags: [],
  sort: undefined,
  chefsMenu: false,
  region: undefined,
}

export const getRestaurantListQueryVariables = ({
  selectedRegion,
  filters,
  search,
  tagsAndNames,
  endCursor,
  first,
  reservableFrom,
  endsOn,
  ids,
}: AllQueryVariables): RestaurantsListQueryVariables => {
  const { tagIds } = getTagAndRestaurantIds(tagsAndNames)
  const allTagIds = [...filters.tags.map(({ id }) => id), ...tagIds]
  const neighboringSlots = addFringeSlots([search.slot], 30, 5)
  const { date } = search

  const rangeStart = date || dayjs.parseZone(reservableFrom)
  const rangeEnd = date || endsOn

  return {
    region_id: selectedRegion?.id,
    sort: filters.sort,
    reservation_filters: {
      startsOn: dayjsToString(rangeStart),
      endsOn: dayjsToString(rangeEnd),
      hours: [...neighboringSlots],
      peopleCount: search.peopleCount,
      reservableType: ReservableEnum.Daily,
    },
    after: endCursor,
    first: first,
    ...(allTagIds.length && { tag_ids: allTagIds }),
    ...(ids !== undefined ? { ids } : {}),
    ...('chefsMenu' in filters && filters.chefsMenu && { with_reservable_extras: true }),
  }
}

export const getTagAndRestaurantIds = tagsAndNames => {
  const tagIds = []
  const restaurantIds = []
  tagsAndNames &&
    tagsAndNames.forEach(({ __typename, id }) => {
      __typename === 'Tag' && tagIds.push(id)
      __typename === 'Restaurant' && restaurantIds.push(id)
    })
  return {
    tagIds,
    restaurantIds,
  }
}

export const getListingBanners = (ongoingFestivalsData?: OngoingFestivalsListQueryResult['data']['ongoingFestivalEditions']) => {
  const { tp } = gt
  return ongoingFestivalsData
    ?.filter(ongoingFestival => ongoingFestival.state === 'SALE')
    ?.map(ongoingFestival => {
      switch (ongoingFestival.festival.code) {
        case FestivalCodesEnum.Rw:
          return (
            <FestivalListingBannerView
              key={ongoingFestival.id}
              styles={rwpStyle}
              title={tp('ListingBanner', 'RestaurantWeek® is on!')}
              subtitle={tp('ListingBanner', "Don't miss the 3-course menu with a complimentary cocktail!")}
              button={{
                href: paths.festivalListing('restaurant-week'),
                label: tp('ListingBanner', 'See restaurants'),
                withArrow: true,
              }}
              leftElement={
                <LeftImageContainer>
                  <LeftImageBottomElement src='/assets/rwp-main-hand.png' alt='Restaurant Week' />
                </LeftImageContainer>
              }
              logo={'/assets/logo/logo-festival-rwp-white.svg'}
              date={`${dateToDaysAndMonths(ongoingFestival.startsAt)} - ${dateToDaysAndMonths(ongoingFestival.endsAt)}`}
            />
          )
        case FestivalCodesEnum.Bw:
          return (
            <FestivalListingBannerView
              key={ongoingFestival.id}
              styles={bwStyle}
              title={tp('ListingBanner', 'Book BreakfastWeek!')}
              subtitle={tp('ListingBanner', "Don't miss the Breakfast Festival in town!")}
              button={{
                href: paths.festivalListing('breakfast-week'),
                label: tp('ListingBanner', 'See restaurants'),
                withArrow: true,
              }}
              leftElement={
                <LeftImageContainer>
                  <LeftImageBottomElement src='/assets/bw-main.png' alt='Breakfast Week' />
                  <LeftImageTopElement src={'/assets/bw-logo.png'} />
                </LeftImageContainer>
              }
              logo={'/assets/logo/logo-festival-rwp.svg'}
              date={`${dateToDaysAndMonths(ongoingFestival.startsAt)} - ${dateToDaysAndMonths(ongoingFestival.endsAt)}`}
            />
          )
        default:
          return null
      }
    })
    .filter(item => item !== null)
}

// TODO: uncomment after release 16.12
export const useRestaurantsInOtherDates = ({ filters }: { filters: AllQueryVariables }) => {
  // eslint-disable-next-line no-unused-vars
  const { data } = useOtherDatesRestaurantsQuery({
    variables: getRestaurantListQueryVariables({ ...filters, first: OTHER_DATES_RESTAURANTS_LIMIT }),
  })

  return { otherDatesRestaurants: { totalCount: 0, nodes: [] } as OtherDatesRestaurantsQuery['otherDatesRestaurants'] }
}
